import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, InputNumber, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'motion/react';
import { Editor } from '@hugerte/hugerte-react';

const ColorSwatch = ({ color }) => (
	<div
		style={{
			width: 36,
			height: 36,
			borderRadius: 8,
			border: '2px solid #e0e0e0',
			backgroundColor: color || '#ffffff',
			flexShrink: 0,
		}}
	/>
);

const editorInit = {
	height: '100%',
	menubar: false,
	branding: false,
	plugins: ['quickbars', 'lists', 'link'],
	toolbar:
		'undo redo fontsizeinput bold italic forecolor backcolor link | styles alignleft aligncenter alignright alignjustify bullist numlist outdent indent',
	placeholder: 'Enter message here...',
	quickbars_selection_toolbar:
		'bold italic forecolor backcolor removeformat link',
	quickbars_image_toolbar: false,
	quickbars_insert_toolbar: false,
	toolbar_mode: 'floating',
	link_default_target: '_blank',
	link_assume_external_targets: true,
	link_context_toolbar: true,
};

const MilestoneSettingsCard = ({
	milestoneColorMap,
	recognitionSettings,
	onSave,
	isSaving,
}) => {
	const [localMap, setLocalMap] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const idCounter = useRef(0);

	const [claimExpiryDays, setClaimExpiryDays] = useState(0);
	const [claimModalTitle, setClaimModalTitle] = useState('');
	const [claimModalMessage, setClaimModalMessage] = useState('');

	useEffect(() => {
		setLocalMap(
			milestoneColorMap.map((item) => ({
				...item,
				_id: idCounter.current++,
			}))
		);
		setClaimExpiryDays(recognitionSettings?.milestoneClaimExpiryDays ?? 0);
		setClaimModalTitle(recognitionSettings?.milestoneClaimModalTitle ?? '');
		setClaimModalMessage(recognitionSettings?.milestoneClaimModalMessage ?? '');
		setHasChanges(false);
	}, [milestoneColorMap, recognitionSettings]);

	const updateEntry = (index, field, value) => {
		const updated = localMap.map((item, i) =>
			i === index ? { ...item, [field]: value } : item
		);
		setLocalMap(updated);
		setHasChanges(true);
	};

	const sortByYears = () => {
		setLocalMap((prev) => [...prev].sort((a, b) => a.years - b.years));
	};

	const addEntry = () => {
		const maxYears = Math.max(...localMap.map((item) => item.years));
		const nextYears = maxYears + 1;

		setLocalMap([
			...localMap,
			{ years: nextYears, color: '#C0A44D', _id: idCounter.current++ },
		]);
		setHasChanges(true);
	};

	const removeEntry = (index) => {
		if (localMap[index].years === 0) return;
		setLocalMap(localMap.filter((_, i) => i !== index));
		setHasChanges(true);
	};

	const handleSave = () => {
		const yearsSet = new Set();
		for (const entry of localMap) {
			if (yearsSet.has(entry.years)) {
				return;
			}

			yearsSet.add(entry.years);

			if (entry.years !== 0 && !entry.color) {
				return;
			}
		}

		onSave({
			milestoneColorMap: localMap.map(({ _id, ...rest }) => rest),
			milestoneClaimExpiryDays: claimExpiryDays,
			milestoneClaimModalTitle: claimModalTitle,
			milestoneClaimModalMessage: claimModalMessage,
		});
	};

	const handleUndo = () => {
		setLocalMap(
			milestoneColorMap.map((item) => ({
				...item,
				_id: idCounter.current++,
			}))
		);
		setClaimExpiryDays(recognitionSettings?.milestoneClaimExpiryDays ?? 0);
		setClaimModalTitle(recognitionSettings?.milestoneClaimModalTitle ?? '');
		setClaimModalMessage(recognitionSettings?.milestoneClaimModalMessage ?? '');
		setHasChanges(false);
	};

	const hasDuplicateYears = () => {
		const years = localMap.map((item) => item.years);
		return new Set(years).size !== years.length;
	};

	const hasInvalidColors = () => {
		return localMap.some((entry) => entry.years !== 0 && !entry.color);
	};

	const isInvalid = hasDuplicateYears() || hasInvalidColors();

	return (
		<div
			style={{
				background: '#fff',
				borderRadius: '14px',
				padding: '24px',
				border: '1px solid #ddd',
				marginBottom: '24px',
			}}
		>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '20px',
				}}
			>
				<div>
					<h3
						style={{
							fontSize: '18px',
							fontWeight: '600',
							color: '#555',
							margin: 0,
						}}
					>
						Milestone Settings
					</h3>
					<p style={{ color: '#595959', fontSize: '13px', margin: '4px 0 0' }}>
						Configure milestone claim options and avatar color mappings.
					</p>
				</div>
				<div style={{ display: 'flex', gap: 8 }}>
					{hasChanges && (
						<Button type="link" onClick={handleUndo}>
							Undo Changes
						</Button>
					)}
					<Tooltip
						title={
							hasDuplicateYears()
								? 'Two color mappings cannot share the same year threshold'
								: null
						}
					>
						<Button
							type="primary"
							onClick={handleSave}
							loading={isSaving}
							disabled={!hasChanges || isInvalid}
							style={{ borderRadius: 8 }}
						>
							Save Changes
						</Button>
					</Tooltip>
				</div>
			</div>

			<div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
				{/* Left: Claim Settings */}
				<div
					style={{
						flex: 1,
						minWidth: 300,
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<h4
						style={{
							fontSize: '14px',
							fontWeight: '600',
							color: '#555',
							margin: '0 0 16px',
						}}
					>
						Reward Claim Settings
					</h4>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 16,
							flex: 1,
						}}
					>
						<div>
							<label
								style={{
									display: 'block',
									fontSize: '13px',
									fontWeight: '500',
									color: '#555',
									marginBottom: 6,
								}}
							>
								Days to Claim Rewards
							</label>
							<InputNumber
								value={claimExpiryDays}
								onChange={(val) => {
									setClaimExpiryDays(val ?? 0);
									setHasChanges(true);
								}}
								min={0}
								style={{ width: '100%' }}
								placeholder="0 (Forever)"
								formatter={(value) =>
									value === 0 || value === '0' ? '0 (Forever)' : value
								}
								parser={(value) =>
									Number.parseInt(value.replace(' (Forever)', ''), 10) || 0
								}
							/>
						</div>
						<div>
							<label
								style={{
									display: 'block',
									fontSize: '13px',
									fontWeight: '500',
									color: '#555',
									marginBottom: 6,
								}}
							>
								Reward Claim Popup Title
							</label>
							<Input
								value={claimModalTitle}
								onChange={(e) => {
									setClaimModalTitle(e.target.value);
									setHasChanges(true);
								}}
								placeholder="Enter popup title"
							/>
						</div>
						<div
							style={{
								flex: 1,
								display: 'flex',
								flexDirection: 'column',
								minHeight: 200,
							}}
						>
							<label
								style={{
									display: 'block',
									fontSize: '13px',
									fontWeight: '500',
									color: '#555',
									marginBottom: 6,
								}}
							>
								Reward Claim Popup Message
							</label>
							<div style={{ flex: 1 }}>
								<Editor
									value={claimModalMessage}
									onEditorChange={(_newValue, editor) => {
										setClaimModalMessage(editor.getContent({ format: 'html' }));
										setHasChanges(true);
									}}
									init={editorInit}
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Vertical divider */}
				<div
					style={{ width: 1, background: '#f0f0f0', alignSelf: 'stretch' }}
				/>

				{/* Right: Color Map */}
				<div style={{ flex: 1, minWidth: 300 }}>
					<h4
						style={{
							fontSize: '14px',
							fontWeight: '600',
							color: '#555',
							margin: '0 0 16px',
						}}
					>
						Avatar Color Map
					</h4>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: '52px minmax(60px, 1fr) minmax(100px, 2fr) 32px',
								gap: 12,
								padding: '0 8px 8px',
								borderBottom: '1px solid #f0f0f0',
								fontSize: '12px',
								fontWeight: '600',
								color: '#595959',
								textTransform: 'uppercase',
								letterSpacing: '0.5px',
							}}
						>
							<span>Preview</span>
							<span>Years</span>
							<span>Color</span>
							<span />
						</div>
						<AnimatePresence mode="popLayout">
							{localMap.map((entry, index) => {
								const isDefault = entry.years === 0;
								const isDuplicate =
									localMap.filter((e) => e.years === entry.years).length > 1;
								const isMissingColor = !isDefault && !entry.color;
								return (
									<motion.div
										key={entry._id}
										layout
										initial={{ opacity: 0, x: -20 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, x: 20, height: 0 }}
										transition={{
											duration: 0.3,
											delay: index * 0.05,
											layout: { type: 'spring', stiffness: 350, damping: 30 },
										}}
										style={{
											display: 'grid',
											gridTemplateColumns: '52px minmax(60px, 1fr) minmax(100px, 2fr) 32px',
											gap: 12,
											alignItems: 'center',
											padding: '8px',
											borderRadius: 8,
											background:
												isDuplicate || isMissingColor ? '#fff2f0' : '#fafafa',
											border:
												isDuplicate || isMissingColor
													? '1px solid #ffccc7'
													: '1px solid transparent',
										}}
									>
										<ColorSwatch color={entry.color} />
										<div>
											{isDefault ? (
												<span
													style={{
														fontSize: '14px',
														fontWeight: '500',
														color: '#666',
													}}
												>
													Default (0)
												</span>
											) : (
												<InputNumber
													min={1}
													value={entry.years}
													onChange={(val) => updateEntry(index, 'years', val)}
													onBlur={sortByYears}
													style={{ width: '100%', borderRadius: 6 }}
													status={isDuplicate ? 'error' : undefined}
												/>
											)}
										</div>
										<div
											style={{ display: 'flex', gap: 8, alignItems: 'center' }}
										>
											<Input
												value={entry.color || ''}
												onChange={(e) =>
													updateEntry(index, 'color', e.target.value)
												}
												placeholder={isDefault ? 'No color (optional)' : '#hex'}
												style={{ borderRadius: 6, flex: 1 }}
												status={isMissingColor ? 'error' : undefined}
											/>
											<input
												type="color"
												value={entry.color || '#ffffff'}
												onChange={(e) =>
													updateEntry(index, 'color', e.target.value)
												}
												style={{
													width: 36,
													height: 32,
													padding: 0,
													border: '1px solid #d9d9d9',
													borderRadius: 6,
													cursor: 'pointer',
													background: 'none',
												}}
											/>
										</div>
										<div style={{ textAlign: 'center' }}>
											{!isDefault && (
												<Button
													type="text"
													danger
													icon={<DeleteOutlined />}
													onClick={() => removeEntry(index)}
													size="small"
												/>
											)}
										</div>
									</motion.div>
								);
							})}
						</AnimatePresence>
					</div>
					<motion.div
						style={{ marginTop: 16 }}
						whileHover={{ scale: 1.01 }}
						whileTap={{ scale: 0.99 }}
					>
						<Button
							type="dashed"
							icon={<PlusOutlined />}
							onClick={addEntry}
							block
							style={{ borderRadius: 8, height: 40 }}
						>
							Add Color Mapping
						</Button>
					</motion.div>
				</div>
			</div>
		</div>
	);
};

export default MilestoneSettingsCard;
